/**
 * Collection Progress Bar
 *
 * Segmented progress bar showing collection target status breakdown
 * with percentage indicator.
 */

import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { StatusCountEntry } from '../../types/collection-targets'

interface CollectionProgressBarProps {
  statusCounts: StatusCountEntry[]
  totalTargets: number
  progressPercentage: number
}

const SEGMENT_COLOURS: Record<string, string> = {
  pending: 'bg-slate-300',
  in_progress: 'bg-blue-400',
  collecting: 'bg-blue-500',
  collected: 'bg-green-400',
  partial: 'bg-orange-400',
  failed: 'bg-red-500',
  client_halted: 'bg-orange-500',
  cancelled: 'bg-slate-400',
  ingested: 'bg-green-600',
}

export function CollectionProgressBar({
  statusCounts,
  totalTargets,
  progressPercentage,
}: CollectionProgressBarProps): ReactElement | null {
  const { t } = useTranslation('cases')

  if (totalTargets === 0) return null

  return (
    <div className="space-y-2">
      <div className="items-centre flex justify-between text-xs">
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {t('collections.targetMonitoring.totalTargets')}: {totalTargets}
        </span>
        <span className="font-medium text-fuchsia-600 dark:text-fuchsia-400">
          {t('collections.targetMonitoring.progressPercentage', {
            percentage: Math.round(progressPercentage),
          })}
        </span>
      </div>

      {/* Segmented bar */}
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        {statusCounts
          .filter((entry) => entry.count > 0)
          .map((entry) => {
            const pct = (entry.count / totalTargets) * 100
            const colour = SEGMENT_COLOURS[entry.status] ?? 'bg-slate-300'
            return (
              <div
                key={entry.status}
                className={`${colour} transition-all duration-300`}
                style={{ width: `${String(pct)}%` }}
                title={`${entry.status}: ${String(entry.count)}`}
              />
            )
          })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {statusCounts
          .filter((entry) => entry.count > 0)
          .map((entry) => {
            const colour = SEGMENT_COLOURS[entry.status] ?? 'bg-slate-300'
            const label = entry.status
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
            return (
              <div
                key={entry.status}
                className="items-centre flex gap-1.5 text-xs text-slate-600 dark:text-slate-400"
              >
                <span className={`h-2 w-2 rounded-full ${colour}`} />
                <span>
                  {label}: {entry.count}
                </span>
              </div>
            )
          })}
      </div>
    </div>
  )
}
